import { normalizeRenderConfig, sortReportRows } from "./renderConfig";
import { resolveAgentServiceTicketCount } from "./agentServiceTimeMetrics";
import { formatNumber } from "./helpers";

const SUMMARY_HEADERS = Object.freeze([
  "Branch",
  "Service",
  "Agent",
  "Completed Tickets",
  "Total Service Time",
  "Ticket Share",
  "Share of Service Time",
  "Avg / Ticket",
  "Service Avg",
  "Vs Service Avg %",
  "Median",
  "P90",
  "Min Ticket Time",
  "Max Ticket Time",
  "Active Days",
  "Tickets / Active Day",
  "Flags",
]);

const DAILY_HEADERS = Object.freeze(["Branch", "Service", "Agent", "Day", "Daily Average"]);

const toPrimitiveNumber = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

const resolveServiceTimeShare = (row = {}) => toPrimitiveNumber(row.serviceTimeShare ?? row.totalPct);

const getServiceRows = (branch, service) =>
  (service.agentsData || [])
    .filter((agent) => agent?.sum)
    .map((agent) => ({
      ...agent,
      branchName: branch.branchName,
      serviceName: service.serviceName || agent.serviceName || "",
    }));

export const getAgentServiceTimeRowsByService = (branch, renderConfig) =>
  (branch.servicesData || []).map((service) => ({
    serviceName: service.serviceName || "",
    rows: sortReportRows(getServiceRows(branch, service), renderConfig),
  }));

export const flattenAgentServiceTimeRows = (branches = []) =>
  branches.flatMap((branch) =>
    (branch.servicesData || []).flatMap((service) => getServiceRows(branch, service))
  );

export const getAgentServiceTimeRowsByBranch = (branch, renderConfig) => {
  const config = normalizeRenderConfig(renderConfig);
  return getAgentServiceTimeRowsByService(branch, config).flatMap((service) => service.rows);
};

export const getAgentServiceTimeSummaryCsvRows = (branches = [], renderConfig) =>
  branches.flatMap((branch) =>
    getAgentServiceTimeRowsByBranch(branch, renderConfig).map((row) => [
      row.branchName || "",
      row.serviceName || "",
      row.agentName || "",
      resolveAgentServiceTicketCount(row),
      toPrimitiveNumber(row.sum),
      toPrimitiveNumber(row.ticketShare),
      resolveServiceTimeShare(row),
      toPrimitiveNumber(row.avgPerTicket ?? row.average),
      toPrimitiveNumber(row.serviceAverage),
      toPrimitiveNumber(row.avgVsServiceAveragePct),
      toPrimitiveNumber(row.medianServiceTime ?? row.median),
      toPrimitiveNumber(row.p90ServiceTime ?? row.p90),
      toPrimitiveNumber(row.min),
      toPrimitiveNumber(row.max),
      toPrimitiveNumber(row.activeDays),
      toPrimitiveNumber(row.ticketsPerActiveDay),
      Array.isArray(row.flags) ? row.flags.join(" | ") : row.flags || "",
    ])
  );

export const getAgentServiceTimeDailyCsvRows = (branches = [], renderConfig) =>
  branches.flatMap((branch) =>
    getAgentServiceTimeRowsByBranch(branch, renderConfig).flatMap((row) =>
      (row.chart || []).map((point) => [
        row.branchName || "",
        row.serviceName || "",
        row.agentName || "",
        point.date || "",
        toPrimitiveNumber(point.dailyAverage),
      ])
    )
  );

const getDayLabels = (rows, getPoints) =>
  Array.from(
    new Set(
      rows.flatMap((row) =>
        (getPoints(row) || []).map((point) => point.date).filter((date) => date !== undefined && date !== null && date !== "")
      )
    )
  );

export const getAgentServiceTimeMergedCsvHeaders = (branches = [], renderConfig) => {
  const rows = branches.flatMap((branch) => getAgentServiceTimeRowsByBranch(branch, renderConfig));
  return [...SUMMARY_HEADERS, ...getDayLabels(rows, (row) => row.chart).map((day) => `Day ${day}`)];
};

export const getAgentServiceTimeMergedCsvRows = (branches = [], renderConfig) => {
  const rows = branches.flatMap((branch) => getAgentServiceTimeRowsByBranch(branch, renderConfig));
  const dayLabels = getDayLabels(rows, (row) => row.chart);
  return rows.map((row) => {
    const valuesByDay = Object.fromEntries(
      (row.chart || []).map((point) => [point.date, toPrimitiveNumber(point.dailyAverage)])
    );
    return [
      row.branchName || "",
      row.serviceName || "",
      row.agentName || "",
      resolveAgentServiceTicketCount(row),
      toPrimitiveNumber(row.sum),
      toPrimitiveNumber(row.ticketShare),
      resolveServiceTimeShare(row),
      toPrimitiveNumber(row.avgPerTicket ?? row.average),
      toPrimitiveNumber(row.serviceAverage),
      toPrimitiveNumber(row.avgVsServiceAveragePct),
      toPrimitiveNumber(row.medianServiceTime ?? row.median),
      toPrimitiveNumber(row.p90ServiceTime ?? row.p90),
      toPrimitiveNumber(row.min),
      toPrimitiveNumber(row.max),
      toPrimitiveNumber(row.activeDays),
      toPrimitiveNumber(row.ticketsPerActiveDay),
      Array.isArray(row.flags) ? row.flags.join(" | ") : row.flags || "",
      ...dayLabels.map((day) => valuesByDay[day] ?? ""),
    ];
  });
};

const escapeCsvValue = (value) => {
  if (value === null || value === undefined) return "";
  const stringValue = String(value);
  return /[",\n\r]/.test(stringValue) ? `"${stringValue.replace(/"/g, '""')}"` : stringValue;
};

const formatCsvHeader = (header) => {
  if (typeof header !== "string") return header;
  if (header === "Day") return "Business Day";
  if (header.startsWith("Day ")) return `Business ${header}`;
  return header;
};

const formatCsvCell = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return value;

  return formatNumber(numericValue, { useGrouping: false });
};

export const buildCsv = (headers, rows) =>
  [
    headers.map(formatCsvHeader),
    ...rows.map((row) => row.map((value) => formatCsvCell(value))),
  ]
    .map((row) => row.map(escapeCsvValue).join(","))
    .join("\n");

const triggerCsvDownload = (filename, csv) => {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
};

export const downloadAgentServiceTimeCsvFiles = ({ data, renderConfig }) => {
  triggerCsvDownload(
    "branch_agent_service_time_per_service.csv",
    buildCsv(
      getAgentServiceTimeMergedCsvHeaders(data, renderConfig),
      getAgentServiceTimeMergedCsvRows(data, renderConfig)
    )
  );
};
